import { useEffect } from "react"
import { AlertCircle, Loader2 } from "lucide-react"
import { useBidsStore } from "@/stores/bids-store"
import { useMyWorkStore } from "@/stores/my-work-store"
import { useOnboardingStore } from "@/stores/onboarding-store"
import { useAuthStore } from "@/stores/auth-store"
import { useDashboardStore } from "@/stores/dashboard-store"
import { OnboardingChecklist, ONBOARDING_STEPS } from "@/components/OnboardingChecklist"
import { MfaPromptBanner } from "@/components/MfaPromptBanner"
import { HomeGreeting } from "@/components/dashboard/HomeGreeting"
import { HomePrompt } from "@/components/dashboard/HomePrompt"
import { useIsRoleLoaded, useIsViewOnly } from "@/hooks/useViewOnly"
import { NeedsYou } from "@/components/dashboard/NeedsYou"
import { DeadlineList } from "@/components/dashboard/DeadlineList"
import { WorkingOnIt } from "@/components/dashboard/WorkingOnIt"
import { CreateBidDialog } from "@/components/CreateBidDialog"

// Home, top to bottom: greeting and a prompt box into the Assistant, then
// "Needs you" (the user's top work items, with insights as "Worth a look"),
// "Closing soon" (active bid deadlines) and "Working on it" (running AI jobs).
// The full queue lives on /app/my-work.
export function HomePage() {
  const { items, comments, isLoading, error, fetchMyWork } = useMyWorkStore()
  const ensureAllBids = useBidsStore((state) => state.ensureAllBids)
  const bids = useBidsStore((state) => state.bids)
  const isLoadingBids = useBidsStore((state) => state.isLoadingBids)
  const { dismissedAt, steps, fetchStatus: fetchOnboarding } = useOnboardingStore()
  const role = useAuthStore((s) => s.role)
  const fullName = useAuthStore((s) => s.user?.user_metadata?.["full_name"] as string | undefined)
  const fetchInsights = useDashboardStore((s) => s.fetchInsights)

  // Every dep here is a stable Zustand action ref, so this runs once per mount
  // (F8). The "only fetch bids if we don't have them" decision lives in
  // `ensureAllBids`.
  useEffect(() => {
    void fetchMyWork()
    void fetchOnboarding()
    void fetchInsights()
    void ensureAllBids()
  }, [fetchMyWork, fetchOnboarding, fetchInsights, ensureAllBids])

  // Deadline counts derive from the bids store, which loads separately from
  // My Work items. "Still fetching + none yet" is loading, so the overdue and
  // due-soon counts don't flash 0 before bids arrive.
  const bidsLoading = isLoadingBids && bids.length === 0

  const isAdmin = role === 'admin'
  const visibleSteps = ONBOARDING_STEPS.filter((s) => !s.adminOnly || isAdmin)
  const allVisibleComplete = visibleSteps.every((s) => steps[s.key])
  const showOnboarding = !dismissedAt && !allVisibleComplete
  // Mirrors the Bids page, which hides New Bid from viewers.
  const canCreateBid = role !== 'viewer'
  const isViewOnly = useIsViewOnly()
  const isRoleLoaded = useIsRoleLoaded()

  const handleBidCreated = () => {
    void fetchOnboarding()
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-auto px-6 py-4">
        <div className="mx-auto max-w-4xl space-y-6">
          <div className="space-y-4">
            <HomeGreeting
              fullName={fullName}
              action={
                canCreateBid ? (
                  // Same trigger as the Bids page, so the button matches app-wide.
                  <CreateBidDialog onSuccess={handleBidCreated} />
                ) : undefined
              }
            />
            {/* The prompt starts an Assistant chat, which a viewer cannot open. */}
            {isRoleLoaded && !isViewOnly && <HomePrompt />}
          </div>
          {isLoading && <LoadingSkeleton />}
          {error && !isLoading && <ErrorMessage message={error} />}
          {!isLoading && !error && (
            <>
              <MfaPromptBanner />
              {showOnboarding && <OnboardingChecklist />}
              <NeedsYou
                items={items}
                comments={comments ?? []}
                bids={bids}
                deadlinesLoading={bidsLoading}
              />
              {bids.length > 0 && <DeadlineList bids={bids} />}
              <WorkingOnIt />
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function LoadingSkeleton() {
  return (
    <div className="flex items-center justify-center py-16">
      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
    </div>
  )
}

function ErrorMessage({ message }: { message: string }) {
  return (
    <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-destructive/10 text-destructive text-sm">
      <AlertCircle className="h-4 w-4 shrink-0" />
      {message}
    </div>
  )
}
